/**
 * <ff-variant-select> — accessible custom dropdown for product variants.
 *
 * Why custom (not native <select>): brand-styled placeholder + option layout,
 * keyboard nav matching WAI-ARIA 1.2 listbox pattern, no native UA chrome.
 *
 * Sync contract:
 * - Updates the nearest ancestor `input[name="id"][data-productid="{id}"]`
 *   value with the chosen variant id and dispatches `input` + `change`.
 * - This keeps `assets/variants.js` and the cart form in sync without
 *   requiring a native <select>.
 */
(function () {
  if (window.customElements && customElements.get('ff-variant-select')) return;

  const KEYS = {
    UP: 'ArrowUp',
    DOWN: 'ArrowDown',
    HOME: 'Home',
    END: 'End',
    ENTER: 'Enter',
    SPACE: ' ',
    ESC: 'Escape',
    TAB: 'Tab'
  };

  class FFVariantSelect extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = -1;
      this.selectedId = null;
      this.onDocClick = this.onDocClick.bind(this);
      this.onToggleClick = () => this.togglePanel();
      this.onToggleKeydown = (e) => this.onToggleKey(e);
      this.onListKeydown = (e) => this.onListKey(e);
      this.optionHandlers = [];
    }

    connectedCallback() {
      this.toggle = this.querySelector('.ff-variant-select__toggle');
      this.toggleText = this.querySelector('.ff-variant-select__toggle-text');
      this.list = this.querySelector('.ff-variant-select__list');
      this.options = Array.from(this.querySelectorAll('.ff-variant-select__option'));
      this.placeholder = this.toggle?.dataset.placeholder || '';

      if (!this.toggle || !this.list || this.options.length === 0) return;

      this.toggle.setAttribute('data-state', 'placeholder');

      this.toggle.addEventListener('click', this.onToggleClick);
      this.toggle.addEventListener('keydown', this.onToggleKeydown);
      this.list.addEventListener('keydown', this.onListKeydown);

      this.options.forEach((opt, idx) => {
        const onClick = () => this.selectByIndex(idx);
        const onEnter = () => this.setActive(idx);
        this.optionHandlers.push({ opt, onClick, onEnter });
        opt.addEventListener('click', onClick);
        opt.addEventListener('mouseenter', onEnter);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      this.toggle?.removeEventListener('click', this.onToggleClick);
      this.toggle?.removeEventListener('keydown', this.onToggleKeydown);
      this.list?.removeEventListener('keydown', this.onListKeydown);
      this.optionHandlers.forEach(({ opt, onClick, onEnter }) => {
        opt.removeEventListener('click', onClick);
        opt.removeEventListener('mouseenter', onEnter);
      });
      this.optionHandlers = [];
    }

    isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    togglePanel() {
      this.isOpen() ? this.close() : this.open();
    }

    open() {
      this.toggle.setAttribute('aria-expanded', 'true');
      this.list.hidden = false;
      const selectedIdx = this.options.findIndex(
        (o) => o.getAttribute('aria-selected') === 'true' && o.getAttribute('aria-disabled') !== 'true'
      );
      const firstEnabledIdx = this.options.findIndex((o) => o.getAttribute('aria-disabled') !== 'true');
      const startIdx = selectedIdx >= 0 ? selectedIdx : firstEnabledIdx;
      this.setActive(startIdx >= 0 ? startIdx : 0);
      this.options[this.activeIndex]?.focus();
      document.addEventListener('click', this.onDocClick);
    }

    close({ focusToggle = false } = {}) {
      this.toggle.setAttribute('aria-expanded', 'false');
      this.list.hidden = true;
      document.removeEventListener('click', this.onDocClick);
      if (focusToggle) this.toggle.focus();
    }

    onDocClick(e) {
      if (!this.contains(e.target)) this.close();
    }

    isEnabled(index) {
      const opt = this.options[index];
      return Boolean(opt) && opt.getAttribute('aria-disabled') !== 'true';
    }

    findEnabledIndex(start, step) {
      for (let i = start; i >= 0 && i < this.options.length; i += step) {
        if (this.isEnabled(i)) return i;
      }
      return -1;
    }

    setActive(index) {
      if (index < 0 || index >= this.options.length || !this.isEnabled(index)) return;
      this.options.forEach((o) => o.classList.remove('is-active'));
      this.activeIndex = index;
      const opt = this.options[index];
      opt.classList.add('is-active');
      opt.focus({ preventScroll: false });
    }

    selectByIndex(index) {
      const opt = this.options[index];
      if (!opt || opt.getAttribute('aria-disabled') === 'true') return;

      this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
      opt.setAttribute('aria-selected', 'true');

      const variantId = opt.dataset.variantId;
      const title = opt.dataset.variantTitle || '';

      this.selectedId = variantId;
      this.toggleText.textContent = title;
      this.toggle.setAttribute('data-state', 'selected');

      this.syncHiddenInput(variantId);
      this.close({ focusToggle: true });
    }

    syncHiddenInput(variantId) {
      const wrapper = this.closest('[id^="product-form-"]') || this.parentElement;
      if (!wrapper) return;
      const input = wrapper.querySelector(`input[name="id"][data-productid="${this.dataset.productId}"]`);
      if (!input) return;
      input.value = variantId;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onToggleKey(e) {
      if ([KEYS.DOWN, KEYS.UP, KEYS.ENTER, KEYS.SPACE].includes(e.key)) {
        e.preventDefault();
        this.open();
      }
    }

    onListKey(e) {
      switch (e.key) {
        case KEYS.DOWN: {
          e.preventDefault();
          const next = this.findEnabledIndex(this.activeIndex + 1, 1);
          if (next >= 0) this.setActive(next);
          break;
        }
        case KEYS.UP: {
          e.preventDefault();
          const prev = this.findEnabledIndex(this.activeIndex - 1, -1);
          if (prev >= 0) this.setActive(prev);
          break;
        }
        case KEYS.HOME: {
          e.preventDefault();
          const first = this.findEnabledIndex(0, 1);
          if (first >= 0) this.setActive(first);
          break;
        }
        case KEYS.END: {
          e.preventDefault();
          const last = this.findEnabledIndex(this.options.length - 1, -1);
          if (last >= 0) this.setActive(last);
          break;
        }
        case KEYS.ENTER:
        case KEYS.SPACE:
          e.preventDefault();
          this.selectByIndex(this.activeIndex);
          break;
        case KEYS.ESC:
          e.preventDefault();
          this.close({ focusToggle: true });
          break;
        case KEYS.TAB:
          this.close();
          break;
      }
    }
  }

  customElements.define('ff-variant-select', FFVariantSelect);
})();
